<!--
    רקע המערכת - one of the very faint designs of Common/page-backdrops.js, painted on the gray behind the cards.
    App.vue mounts it under the page: fixed to the screen, behind everything (z-index -1, never takes a click), while
    .page-body turns transparent - so the cards scroll over it and nothing repaints on scroll. With `preview` it fills
    its parent instead (the tiles of Settings/PageBackgroundSettings.vue), `strength` times stronger.
-->
<template>
    <div class="pageBackdrop"
         :class="{ pageBackdropPreview: preview }"
         :data-backdrop="design ? design.key : ''"
         aria-hidden="true">

        <!-- the drawing over the gray; keyed, so a newly picked design fades in -->
        <div v-if="design"
             :key="design.key"
             class="pageBackdropArt"
             :style="{ background: glows }">

            <svg v-for="band in bands"
                 :key="band.place"
                 class="pageBackdropBand"
                 :class="'pageBackdrop' + band.place"
                 :viewBox="'0 0 ' + band.width + ' ' + band.height"
                 :preserveAspectRatio="band.place == 'Top' ? 'xMidYMin slice' : 'xMidYMax slice'"
                 focusable="false">
                <defs>
                    <linearGradient v-for="layer in band.fills"
                                    :key="layer.id"
                                    :id="layer.id"
                                    x1="0"
                                    y1="0"
                                    :x2="layer.across ? 1 : 0"
                                    :y2="layer.across ? 0 : 1">
                        <stop offset="0" :stop-color="layer.colors[0]" :stop-opacity="layer.opacities[0]" />
                        <stop offset="1" :stop-color="layer.colors[1]" :stop-opacity="layer.opacities[1]" />
                    </linearGradient>
                </defs>
                <path v-for="layer in band.layers"
                      :key="layer.id"
                      :d="layer.d"
                      :fill="layer.fill"
                      :stroke="layer.stroke"
                      :stroke-opacity="layer.strokeOpacity"
                      :stroke-width="layer.stroke ? 1.4 : null"
                      :vector-effect="layer.stroke ? 'non-scaling-stroke' : null" />
            </svg>

        </div>

    </div>
</template>

<script>

import { backdropByKey, backdropGlows, smoothCurve } from './page-backdrops';

export default {

    name: 'PageBackdrop',

    props: {
        // a key of PAGE_BACKDROPS
        backdrop: { type: String, default: '' },
        preview: { type: Boolean, default: false },
        strength: { type: Number, default: 1 },
    },

    computed: {

        design() {
            return backdropByKey(this.backdrop);
        },

        glows() {
            return backdropGlows(this.design, this.strength);
        },

        bands() {

            const design = this.design;
            if (!design)
                return [];

            const s = this.strength;
            const uid = 'pageBackdrop' + this._uid;

            return ['top', 'bottom'].filter(place => design[place]).map(place => {

                const band = design[place];
                const Place = place == 'top' ? 'Top' : 'Bottom';
                const layers = band.layers.map((layer, i) => {

                    const id = uid + Place + i;
                    const d = smoothCurve(layer.points);

                    if (layer.kind == 'line')
                        return { id: id, d: d, fill: 'none', stroke: layer.color, strokeOpacity: Math.min(1, layer.opacity * s) };

                    return {
                        id: id,
                        d: d + ' L' + band.width + ',' + band.height + ' L0,' + band.height + ' Z',
                        fill: 'url(#' + id + ')',
                        colors: layer.colors,
                        opacities: layer.opacities.map(o => Math.min(1, o * s)),
                        across: layer.across,
                    };

                });

                return { place: Place, width: band.width, height: band.height, layers: layers, fills: layers.filter(l => l.colors) };

            });

        },

    },

}

</script>

<style>
.pageBackdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    overflow: hidden;
    pointer-events: none;
    /* the gray of .page-body (static/css/universal/style.css), which is transparent while this is on */
    background-color: #f6f7fb;
    /* where the top band starts: under the header (.page-body's margin-top) */
    --pageBackdropTop: 80px;
}

@media (max-width: 991px) {
    .pageBackdrop {
        --pageBackdropTop: 60px;
    }
}

.pageBackdrop.pageBackdropPreview {
    position: absolute;
    z-index: 0;
    --pageBackdropTop: 13%;
}

.pageBackdrop .pageBackdropArt {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    animation: pageBackdropIn .8s ease-out;
}

.pageBackdrop.pageBackdropPreview .pageBackdropArt {
    animation: none;
}

@keyframes pageBackdropIn {
    from {
        opacity: 0;
    }
}

@media (prefers-reduced-motion: reduce) {
    .pageBackdrop .pageBackdropArt {
        animation: none;
    }
}

.pageBackdrop .pageBackdropBand {
    position: absolute;
    left: 0;
    display: block;
    width: 100%;
}

.pageBackdrop .pageBackdropTop {
    top: var(--pageBackdropTop);
    height: 30%;
}

.pageBackdrop .pageBackdropBottom {
    bottom: 0;
    height: 48%;
}

@media (max-width: 767px) {
    .pageBackdrop .pageBackdropTop {
        height: 22%;
    }

    .pageBackdrop .pageBackdropBottom {
        height: 40%;
    }
}
</style>
